import React, { useState, useEffect, useRef } from 'react';
import {
  Tabs,
  Table,
  Tag,
  Button,
  Drawer,
  Form,
  Input,
  InputNumber,
  Select,
  DatePicker,
  Upload,
  message,
  Modal,
  Card,
  Statistic,
  Row,
  Col,
  Space,
  Typography,
  Badge,
  Progress,
  Timeline,
  Divider,
  Alert,
  Descriptions,
  Spin,
  Tooltip
} from 'antd';
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import {
  BgColorsOutlined,
  DollarOutlined,
  ToolOutlined,
  CarOutlined,
  EyeOutlined,
  EditOutlined,
  PlusOutlined,
  UploadOutlined,
  ExclamationCircleOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  FileTextOutlined,
  FilterOutlined,
  UserOutlined,
  DownloadOutlined,
  WhatsAppOutlined,
  CopyOutlined,
  PhoneOutlined
} from '@ant-design/icons';
import axios from 'axios';
import constants from '../../constant';
import { useAuth } from '../../context/authContext';
import moment from 'moment';
import DesignPrintSummary from './DesignPrintSummary';

const { TabPane } = Tabs;
const { Option } = Select;
const { TextArea } = Input;
const { Title, Text } = Typography;

// Job Detail View Component
const JobDetailView = ({ job, visible, onClose }) => {
  const [loading, setLoading] = useState(false);
  
  if (!job) return null;

  // Helper function to safely get decimal values
  const getDecimalValue = (value) => {
    if (!value) return 0;
    if (value.$numberDecimal) return parseFloat(value.$numberDecimal);
    return parseFloat(value) || 0;
  };

  const getStatusColor = (status) => {
    const colors = {
      'PENDING_DESIGN': 'blue',
      'PENDING_ACCOUNTS': 'orange', 
      'PENDING_PRODUCTION': 'purple',
      'PENDING_DISPATCH': 'cyan',
      'DISPATCHED': 'green',
      'DELIVERED': 'success',
      'COMPLETED': 'green'
    };
    return colors[status] || 'default';
  };

  const getPriorityColor = (priority) => {
    const colors = { 'HIGH': 'red', 'MEDIUM': 'orange', 'LOW': 'green' };
    return colors[priority] || 'default';
  };

  return (
    <Drawer
      title="Job Details"
      placement="right"
      onClose={() => onClose(false)}
      open={visible}
      width={700}
      footer={
        <div style={{ textAlign: 'right' }}>
          <Button onClick={() => onClose(false)} style={{ marginRight: 8 }}>
            Close
          </Button>
        </div>
      }
    >
      <Spin spinning={loading}>
        <Card className="mb-4">
          <div className="flex justify-between items-center mb-4">
            <Title level={4}>Job Summary</Title>
            <Space>
              <Tag color={getStatusColor(job.currentStatus)}>
                {job.currentStatus?.replace('_', ' ')}
              </Tag>
              <Tag color={getPriorityColor(job.priority)}>
                {job.priority}
              </Tag>
              {job.isUrgent && <Tag color="red">URGENT</Tag>}
            </Space>
          </div>
          <Descriptions bordered column={2}>
            <Descriptions.Item label="Job ID" span={1}>
              <Text code>{job._id?.slice(-8)}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="Order ID" span={1}>
              <Text code>{job.orderId?._id?.slice(-8)}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="Product Code">
              {job.productCode}
            </Descriptions.Item>
            <Descriptions.Item label="Product Name">
              {job.productName || 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="Original Quantity">
              {job.originalQuantity}
            </Descriptions.Item>
            <Descriptions.Item label="Expected Dispatch">
              {job.expectedDispatchDate ? moment(job.expectedDispatchDate).format('DD/MM/YYYY') : 'N/A'}
            </Descriptions.Item>
          </Descriptions>
        </Card>

        {/* Customer Information Section */}
        <Card className="mb-4">
          <Title level={4}>👤 Customer Information</Title>
          <Descriptions bordered column={2}>
            <Descriptions.Item label="Customer Name">
              {job.orderId?.leadId?.name || 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="Company Name">
              {job.orderId?.leadId?.company || 'No Company'}
            </Descriptions.Item>
            <Descriptions.Item label="Primary Phone">
              {job.orderId?.primaryPhone || 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="Secondary Phone">
              {job.orderId?.leadId?.secondaryPhone || 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="Customer City">
              {job.orderId?.leadId?.city || job.orderId?.city || 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="Lead ID">
              {job.orderId?.leadId?._id?.slice(-8) || 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="Address" span={2}>
              {(job.orderId?.flat || job.orderId?.area || job.orderId?.city || job.orderId?.state) ? 
                `${job.orderId.flat || ''}, ${job.orderId.area || ''}, ${job.orderId.city || ''}, ${job.orderId.state || ''} - ${job.orderId.pincode || ''}`.replace(/^,\s*|,\s*$/g, '').replace(/,\s*,/g, ',') 
                : 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="Order Priority">
              <Tag color={getPriorityColor(job.orderId?.orderPriority)}>
                {job.orderId?.orderPriority || 'MEDIUM'}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Order Date">
              {job.orderId?.createdAt ? moment(job.orderId.createdAt).format('DD/MM/YYYY') : 'N/A'}
            </Descriptions.Item>
          </Descriptions>
        </Card>

        {/* Financial Information Section */}
        <Card className="mb-4">
          <Title level={4}>💰 Financial Information</Title>
          <Descriptions bordered column={2}>
            <Descriptions.Item label="Final Amount">
              ₹{getDecimalValue(job.orderId?.finalAmount)?.toLocaleString('en-IN') || 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="Advance Amount">
              ₹{getDecimalValue(job.orderId?.advanceAmount)?.toLocaleString('en-IN') || '0'}
            </Descriptions.Item>
            <Descriptions.Item label="Balance Amount">
              ₹{(getDecimalValue(job.orderId?.finalAmount) - getDecimalValue(job.orderId?.advanceAmount))?.toLocaleString('en-IN') || 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="Payment Status">
              <Tag color={
                (!getDecimalValue(job.orderId?.finalAmount)) ? 'default' :
                (getDecimalValue(job.orderId?.advanceAmount) >= getDecimalValue(job.orderId?.finalAmount)) ? 'green' :
                (getDecimalValue(job.orderId?.advanceAmount) > 0) ? 'orange' : 'red'
              }>
                {(!getDecimalValue(job.orderId?.finalAmount)) ? 'No Amount Set' :
                 (getDecimalValue(job.orderId?.advanceAmount) >= getDecimalValue(job.orderId?.finalAmount)) ? 'Fully Paid' :
                 (getDecimalValue(job.orderId?.advanceAmount) > 0) ? 'Partially Paid' : 'Unpaid'}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Amount with GST">
              ₹{getDecimalValue(job.orderId?.amountWithGST)?.toLocaleString('en-IN') || 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="Amount without GST">
              ₹{getDecimalValue(job.orderId?.amountWithoutGST)?.toLocaleString('en-IN') || 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="GST Percentage">
              {job.orderId?.gstPercentage || '18'}%
            </Descriptions.Item>
            <Descriptions.Item label="GST Number">
              {job.orderId?.gstNumber || 'Not Provided'}
            </Descriptions.Item>
            <Descriptions.Item label="Additional Charges">
              ₹{getDecimalValue(job.orderId?.additionalCharges)?.toLocaleString('en-IN') || '0'}
            </Descriptions.Item>
            <Descriptions.Item label="Packaging Charges" span={2}>
              ₹{getDecimalValue(job.orderId?.packagingCharges)?.toLocaleString('en-IN') || '0'}
            </Descriptions.Item>
          </Descriptions>
        </Card>

        {/* Order Assets Section */}
        {job.orderId?.Assets && job.orderId.Assets.length > 0 && (
          <Card className="mb-4">
            <Title level={4}>📎 Order Assets (From Order Creation)</Title>
            <div>
              {job.orderId.Assets.map((asset, index) => (
                <div key={index} style={{ marginBottom: '8px', padding: '8px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                  <a href={asset} target="_blank" rel="noopener noreferrer" style={{ marginRight: '16px' }}>
                    📁 {asset.split('/').pop()}
                  </a>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    Order Asset #{index + 1}
                  </Text>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Design Section */}
        <Card className="mb-4">
          <Title level={4}>🎨 Design Stage</Title>
          <Descriptions bordered column={2}>
            <Descriptions.Item label="Status">
              <Tag color={job.design?.status === 'COMPLETED' ? 'green' : 'blue'}>
                {job.design?.status || 'PENDING'}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Output Quantity">
              {job.design?.outputQuantity || 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="Instructions" span={2}>
              {job.design?.instructions || 'No instructions provided'}
            </Descriptions.Item>
            <Descriptions.Item label="Cutting File">
              {job.design?.cuttingFileName || 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="Printing File">
              {job.design?.printingFileName || 'N/A'}
            </Descriptions.Item>
            {job.design?.designScreenshot && (
              <Descriptions.Item label="Screenshot" span={2}>
                <a href={job.design.designScreenshot} target="_blank" rel="noopener noreferrer">
                  View Design Screenshot
                </a>
              </Descriptions.Item>
            )}
            {job.design?.files && job.design.files.length > 0 && (
              <Descriptions.Item label="Design Files" span={2}>
                <div>
                  {job.design.files.map((file, index) => (
                    <div key={index} style={{ marginBottom: '8px' }}>
                      <a href={file.fileUrl} target="_blank" rel="noopener noreferrer" style={{ marginRight: '16px' }}>
                        📁 {file.fileName}
                      </a>
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        {moment(file.uploadedAt).format('DD/MM/YYYY HH:mm')}
                      </Text>
                    </div>
                  ))}
                </div>
              </Descriptions.Item>
            )}
            {job.design?.comments && (
              <Descriptions.Item label="Comments" span={2}>
                {job.design.comments}
              </Descriptions.Item>
            )}
            {job.design?.designCompletedAt && (
              <Descriptions.Item label="Completed At" span={2}>
                {moment(job.design.designCompletedAt).format('DD/MM/YYYY HH:mm')}
              </Descriptions.Item>
            )}
          </Descriptions>
        </Card>

        {/* Accounts Section */}
        <Card className="mb-4">
          <Title level={4}>💰 Accounts Stage</Title>
          <Descriptions bordered column={2}>
            <Descriptions.Item label="Status">
              <Tag color={job.accounts?.status === 'COMPLETED' ? 'green' : 'orange'}>
                {job.accounts?.status || 'UNRECONCILED'}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Payment Status">
              <Tag color={job.accounts?.paymentStatus === 'COMPLETED' ? 'green' : 'orange'}>
                {job.accounts?.paymentStatus || 'PENDING'}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Proforma Generated">
              <Tag color={job.accounts?.proformaGenerated ? 'green' : 'red'}>
                {job.accounts?.proformaGenerated ? 'Yes' : 'No'}
              </Tag>
            </Descriptions.Item>
            {job.accounts?.proformaUrl && (
              <Descriptions.Item label="Proforma URL">
                <a href={job.accounts.proformaUrl} target="_blank" rel="noopener noreferrer">
                  View Proforma
                </a>
              </Descriptions.Item>
            )}
            {job.accounts?.comments && (
              <Descriptions.Item label="Comments" span={2}>
                {job.accounts.comments}
              </Descriptions.Item>
            )}
            {job.accounts?.accountsCompletedAt && (
              <Descriptions.Item label="Completed At" span={2}>
                {moment(job.accounts.accountsCompletedAt).format('DD/MM/YYYY HH:mm')}
              </Descriptions.Item>
            )}
          </Descriptions>
        </Card>

        {/* Production Section */}
        <Card className="mb-4">
          <Title level={4}>🏭 Production Stage</Title>
          <Descriptions bordered column={2}>
            <Descriptions.Item label="Status">
              <Tag color={job.production?.status === 'COMPLETED' ? 'green' : job.production?.status === 'IN_PROGRESS' ? 'orange' : 'blue'}>
                {job.production?.status || 'PENDING'}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Output Quantity">
              {job.production?.outputQuantity || 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="Damage Quantity">
              {job.production?.damageQuantity || 0}
            </Descriptions.Item>
            {job.production?.productionStartDate && (
              <Descriptions.Item label="Started At">
                {moment(job.production.productionStartDate).format('DD/MM/YYYY HH:mm')}
              </Descriptions.Item>
            )}
            {job.production?.files && job.production.files.length > 0 && (
              <Descriptions.Item label="Production Files" span={2}>
                <div>
                  {job.production.files.map((file, index) => (
                    <div key={index} style={{ marginBottom: '8px' }}>
                      <a href={file.fileUrl} target="_blank" rel="noopener noreferrer" style={{ marginRight: '16px' }}>
                        📁 {file.fileName}
                      </a>
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        {moment(file.uploadedAt).format('DD/MM/YYYY HH:mm')}
                      </Text>
                    </div>
                  ))}
                </div>
              </Descriptions.Item>
            )}
            {job.production?.comments && (
              <Descriptions.Item label="Comments" span={2}>
                {job.production.comments}
              </Descriptions.Item>
            )}
            {job.production?.productionCompletedAt && (
              <Descriptions.Item label="Completed At" span={2}>
                {moment(job.production.productionCompletedAt).format('DD/MM/YYYY HH:mm')}
              </Descriptions.Item>
            )}
          </Descriptions>
        </Card>

        {/* Dispatch Section */}
        <Card className="mb-4">
          <Title level={4}>🚚 Dispatch Stage</Title>
          <Descriptions bordered column={2}>
            <Descriptions.Item label="Status">
              <Tag color={job.dispatch?.status === 'DELIVERED' ? 'green' : job.dispatch?.status === 'DISPATCHED' ? 'blue' : 'orange'}>
                {job.dispatch?.status || 'PENDING'}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Final Quantity">
              {job.dispatch?.finalQuantity || 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="Final Damage">
              {job.dispatch?.finalDamage || 0}
            </Descriptions.Item>
            <Descriptions.Item label="Dispatch Mode">
              {job.dispatch?.dispatchMode || 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="Weight (kg)">
              {job.dispatch?.weight || 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="Number of Boxes">
              {job.dispatch?.numberOfBoxes || 'N/A'}
            </Descriptions.Item>
            {job.dispatch?.trackingNumber && (
              <Descriptions.Item label="Tracking Number" span={2}>
                <Text code>{job.dispatch.trackingNumber}</Text>
              </Descriptions.Item>
            )}
            {job.dispatch?.files && job.dispatch.files.length > 0 && (
              <Descriptions.Item label="Dispatch Files" span={2}>
                <div>
                  {job.dispatch.files.map((file, index) => (
                    <div key={index} style={{ marginBottom: '8px' }}>
                      <a href={file.fileUrl} target="_blank" rel="noopener noreferrer" style={{ marginRight: '16px' }}>
                        📁 {file.fileName}
                      </a>
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        {moment(file.uploadedAt).format('DD/MM/YYYY HH:mm')}
                      </Text>
                    </div>
                  ))}
                </div>
              </Descriptions.Item>
            )}
            {job.dispatch?.comments && (
              <Descriptions.Item label="Comments" span={2}>
                {job.dispatch.comments}
              </Descriptions.Item>
            )}
            {job.dispatch?.dispatchDate && (
              <Descriptions.Item label="Dispatch Date">
                {moment(job.dispatch.dispatchDate).format('DD/MM/YYYY HH:mm')}
              </Descriptions.Item>
            )}
            {job.dispatch?.deliveryDate && (
              <Descriptions.Item label="Delivery Date">
                {moment(job.dispatch.deliveryDate).format('DD/MM/YYYY HH:mm')}
              </Descriptions.Item>
            )}
          </Descriptions>
        </Card>

        {/* Job Notes */}
        {job.notes && job.notes.length > 0 && (
          <Card className="mb-4">
            <Title level={4}>📝 Job Notes</Title>
            <Timeline>
              {job.notes.map((note, index) => (
                <Timeline.Item key={index}>
                  <div>
                    <Text strong>{note.createdBy?.name || 'System'}</Text>
                    <Text type="secondary" className="ml-2">
                      {moment(note.createdAt).format('DD/MM/YYYY HH:mm')}
                    </Text>
                  </div>
                  <div>{note.message}</div>
                </Timeline.Item>
              ))}
            </Timeline>
          </Card>
        )}
      </Spin>
    </Drawer>
  );
};

// Print Summary Component for Design Jobs
const DesignJobSummary = ({ job, onClose }) => {
  const summaryRef = useRef(null);

  if (!job) return null;

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPDF = async () => {
    if (!summaryRef.current) return;

    try {
      const canvas = await html2canvas(summaryRef.current, {
        scale: 1.5,
        logging: false,
        useCORS: true,
      });

      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });

      const imgWidth = 190;
      const pageHeight = 297;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      const scaleFactor = Math.min(1, pageHeight / imgHeight);
      const finalImgWidth = imgWidth * scaleFactor;
      const finalImgHeight = imgHeight * scaleFactor;

      pdf.addImage(imgData, "PNG", 10, 10, finalImgWidth, finalImgHeight);
      pdf.save(`design_summary_${job._id?.slice(-6)}.pdf`);
      
      message.success('PDF downloaded successfully!');
    } catch (error) {
      console.error("Error generating PDF:", error);
      message.error("Failed to generate PDF");
    }
  };

  const getDecimalValue = (value) => {
    if (!value) return 0;
    if (value.$numberDecimal) return parseFloat(value.$numberDecimal);
    return parseFloat(value) || 0;
  };

  return (
    <Modal
      title="Design Job Summary"
      open={true}
      onCancel={onClose}
      width={900}
      // footer={[
      //   <div key="actions" className="print:hidden" style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
      //     <Button onClick={onClose}>Close</Button>
      //     <Button type="primary" icon={<DownloadOutlined />} onClick={handleDownloadPDF}>
      //       Download PDF
      //     </Button>
      //     <Button type="primary" icon={<FileTextOutlined />} onClick={handlePrint}>
      //       Print Summary
      //     </Button>
      //   </div>
      // ]}
    >
      <DesignPrintSummary job={job} onClose={onClose} />
    </Modal>
  );
};

const JobDashboard = () => {
  const { currentUser, roles } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [selectedJob, setSelectedJob] = useState(null);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [statistics, setStatistics] = useState({});
  const [form] = Form.useForm();
  const [filterByOrderId, setFilterByOrderId] = useState(null);
  const [viewDrawerOpen, setViewDrawerOpen] = useState(false);
  const [viewingJob, setViewingJob] = useState(null);
  const [filters, setFilters] = useState({});
  const [proformaModalVisible, setProformaModalVisible] = useState(false);
  const [selectedJobForProforma, setSelectedJobForProforma] = useState(null);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [printSummaryVisible, setPrintSummaryVisible] = useState(false);
  const [printSummaryJob, setPrintSummaryJob] = useState(null);

  // Role-based access control
  const isAdmin = roles?.includes('ADMIN');
  const isAccounts = roles?.includes('ACCOUNTS');
  const isDesign = roles?.includes('DESIGN');
  const isProduction = roles?.includes('PRODUCTION');
  const isDispatch = roles?.includes('DISPATCH');
  const isSales = roles?.includes('SALES');

  // Helper to get the user's main department (for default tab)
  const getUserDepartment = () => {
    if (isDesign) return 'design';
    if (isAccounts) return 'accounts';
    if (isProduction) return 'production';
    if (isDispatch) return 'dispatch';
    return null;
  };

  // Helper function to safely get decimal values (same as Order_v1)
  const getDecimalValue = (value) => {
    if (!value) return 0;
    if (value.$numberDecimal) return parseFloat(value.$numberDecimal);
    return parseFloat(value) || 0;
  };

  // File upload functions
  const handleFileSelection = (event) => {
    const files = Array.from(event.target.files);
    setSelectedFiles(prevFiles => [...prevFiles, ...files]);
  };

  const removeSelectedFile = (index) => {
    setSelectedFiles(prevFiles => prevFiles.filter((_, i) => i !== index));
  };

  const uploadFilesToCloudinary = async (files) => {
    setUploadingFiles(true);
    const uploadedFileUrls = [];

    try {
      const uploadPromises = Array.from(files).map(async (file) => {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("upload_preset", "wdqav8pw");
        formData.append("public_id", `job-${Date.now()}-${file.name}`);
        formData.append("folder", "job_assets");

        const res = await axios.post(
          "https://api.cloudinary.com/v1_1/drb2pi1dv/raw/upload/",
          formData
        );

        return res.data.url + "?publicId=" + res.data.public_id + "&name=" + file.name;
      });

      const results = await Promise.all(uploadPromises);
      uploadedFileUrls.push(...results);
      return uploadedFileUrls;
    } catch (error) {
      console.error("Upload error:", error);
      message.error("Failed to upload files: " + (error.response?.data?.error?.message || error.message));
      return [];
    } finally {
      setUploadingFiles(false);
    }
  };

  // Department-specific states
  const [designJobs, setDesignJobs] = useState([]);
  const [accountsJobs, setAccountsJobs] = useState([]);
  const [productionJobs, setProductionJobs] = useState([]);
  const [dispatchJobs, setDispatchJobs] = useState([]);
  const [orderJobs, setOrderJobs] = useState([]);

  useEffect(() => {
    // Check URL parameters for order filter
    const urlParams = new URLSearchParams(window.location.search);
    const orderId = urlParams.get('orderId');
    if (orderId) {
      setFilterByOrderId(orderId);
      setActiveTab('order-jobs');
    } else {
      // Set default tab based on user role
      if (isAdmin || isSales) {
        setActiveTab('dashboard');
      } else {
        // Only show their department tab
        const dept = getUserDepartment();
        if (dept) setActiveTab(dept);
      }
    }
    fetchStatistics();
    fetchAllJobs();
  }, [isDesign, isAccounts, isProduction, isDispatch, isAdmin, isSales]);

  useEffect(() => {
    if (activeTab !== 'dashboard' && activeTab !== 'order-jobs') {
      fetchJobsByDepartment(activeTab);
    } else if (activeTab === 'order-jobs' && filterByOrderId) {
      fetchJobsByOrderId(filterByOrderId);
    }
  }, [activeTab, filterByOrderId]);

  const fetchStatistics = async () => {
    try {
      const response = await axios.get(`${constants.DOMAIN}/api/v1/jobs/statistics`);
      setStatistics(response.data.data);
    } catch (error) {
      console.error('Failed to fetch statistics:', error);
    }
  };

  const fetchAllJobs = async () => {
    setLoading(true);
    try {
      // Build filter parameters
      const filterParams = new URLSearchParams({ firebaseId: currentUser._delegate.uid });
      if (filters.jobId) filterParams.append('jobId', filters.jobId);
      if (filters.status) filterParams.append('currentStatus', filters.status);
      if (filters.priority) filterParams.append('priority', filters.priority);
      if (filters.fromDate) filterParams.append('fromDate', filters.fromDate);
      if (filters.toDate) filterParams.append('toDate', filters.toDate);
      if (filters.productCode) filterParams.append('productCode', filters.productCode);
      if (filters.customerName) filterParams.append('customerName', filters.customerName);
      if (filters.companyName) filterParams.append('companyName', filters.companyName);
      if (filters.customerPhone) filterParams.append('customerPhone', filters.customerPhone);
      
      const url = `${constants.DOMAIN}/api/v1/jobs?${filterParams.toString()}`;
      const response = await axios.get(url);
      setJobs(response.data.data.jobs);
    } catch (error) {
      message.error('Failed to fetch jobs');
    } finally {
      setLoading(false);
    }
  };

  const fetchJobsByDepartment = async (department) => {
    setLoading(true);
    try {
      let response;
      let url = '';
      
      if (department === 'accounts') {
        // For accounts, fetch all jobs regardless of current status since accounts is independent
        url = `${constants.DOMAIN}/api/v1/jobs`;
      } else {
        url = `${constants.DOMAIN}/api/v1/jobs/department/${department}`;
      }

      // Add filters to URL
      const filterParams = new URLSearchParams();
      if (filters.jobId) filterParams.append('jobId', filters.jobId);
      if (filters.status) filterParams.append('currentStatus', filters.status);
      if (filters.priority) filterParams.append('priority', filters.priority);
      if (filters.fromDate) filterParams.append('fromDate', filters.fromDate);
      if (filters.toDate) filterParams.append('toDate', filters.toDate);
      if (filters.productCode) filterParams.append('productCode', filters.productCode);
      if (filters.customerName) filterParams.append('customerName', filters.customerName);
      if (filters.companyName) filterParams.append('companyName', filters.companyName);
      if (filters.customerPhone) filterParams.append('customerPhone', filters.customerPhone);
      
      const fullUrl = filterParams.toString() ? `${url}?${filterParams.toString()}` : url;
      
      response = await axios.get(fullUrl);
      
      const jobsList = response.data.data.jobs;
      
      switch (department) {
        case 'accounts':
          setAccountsJobs(jobsList);
          break;
        case 'design':
          setDesignJobs(jobsList);
          break;
        case 'production':
          setProductionJobs(jobsList);
          break;
        case 'dispatch':
          setDispatchJobs(jobsList);
          break;
      }
    } catch (error) {
      message.error(`Failed to fetch ${department} jobs`);
    } finally {
      setLoading(false);
    }
  };

  const fetchJobsByOrderId = async (orderId) => {
    setLoading(true);
    try {
      const response = await axios.get(`${constants.DOMAIN}/api/v1/jobs/order/${orderId}`);
      setOrderJobs(response.data.data.jobs);
    } catch (error) {
      message.error('Failed to fetch jobs for this order');
    } finally {
      setLoading(false);
    }
  };

  const handleJobUpdate = async (jobId, updateData, department) => {
    try {
      // Get the actual user ObjectId from Firebase UID
      const userResponse = await axios.get(`${constants.DOMAIN}/api/v1/systemuser/getUser?firebaseId=${currentUser._delegate.uid}`);
      const userId = userResponse.data.data.users[0]?._id;
      
      // Upload files if any are selected
      let fileUrls = [];
      if (selectedFiles.length > 0) {
        fileUrls = await uploadFilesToCloudinary(selectedFiles);
      }
      
      await axios.patch(`${constants.DOMAIN}/api/v1/jobs/${department}/${jobId}`, {
        ...updateData,
        fileUrls: fileUrls,
        userId: userId
      });
      
      message.success(`Job updated successfully${fileUrls.length > 0 ? ` with ${fileUrls.length} files uploaded` : ''}`);
      
      // Reset file selection
      setSelectedFiles([]);
      
      fetchJobsByDepartment(department);
      if (filterByOrderId) {
        fetchJobsByOrderId(filterByOrderId);
      }
      setDrawerVisible(false);
      form.resetFields();
    } catch (error) {
      console.error('Job update error:', error);
      message.error('Failed to update job');
    }
  };

  const openJobDrawer = (job, department) => {
    setSelectedJob({ ...job, department });
    setSelectedFiles([]); // Reset file selection
    setDrawerVisible(true);
    
    // Pre-populate form based on department
    switch (department) {
      case 'design':
        form.setFieldsValue({
          outputQuantity: job.design?.outputQuantity,
          designScreenshot: job.design?.designScreenshot,
          instructions: job.design?.instructions,
          cuttingFileName: job.design?.cuttingFileName,
          printingFileName: job.design?.printingFileName,
          comments: job.design?.comments,
        });
        break;
      case 'accounts':
        form.setFieldsValue({
          status: job.accounts?.status,
          proformaGenerated: job.accounts?.proformaGenerated,
          proformaUrl: job.accounts?.proformaUrl,
          paymentStatus: job.accounts?.paymentStatus,
          comments: job.accounts?.comments,
        });
        break;
      case 'production':
        form.setFieldsValue({
          outputQuantity: job.production?.outputQuantity,
          damageQuantity: job.production?.damageQuantity,
          comments: job.production?.comments,
          status: job.production?.status,
        });
        break;
      case 'dispatch':
        form.setFieldsValue({
          finalQuantity: job.dispatch?.finalQuantity,
          finalDamage: job.dispatch?.finalDamage,
          dispatchMode: job.dispatch?.dispatchMode,
          weight: job.dispatch?.weight,
          numberOfBoxes: job.dispatch?.numberOfBoxes,
          trackingNumber: job.dispatch?.trackingNumber,
          status: job.dispatch?.status,
          comments: job.dispatch?.comments,
        });
        break;
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'PENDING_DESIGN': 'blue',
      'PENDING_ACCOUNTS': 'orange',
      'PENDING_PRODUCTION': 'purple',
      'PENDING_DISPATCH': 'cyan',
      'DISPATCHED': 'green',
      'DELIVERED': 'success',
      'COMPLETED': 'green'
    };
    return colors[status] || 'default';
  };

  const getPriorityColor = (priority) => {
    const colors = { 'HIGH': 'red', 'MEDIUM': 'orange', 'LOW': 'green' };
    return colors[priority] || 'default';
  };

  const handleViewJob = (job) => {
    setViewingJob(job);
    setViewDrawerOpen(true);
  };

  // Copy job details to clipboard
  const handleCopyJob = (job) => {
    const jobDetails = `Job ID: ${job._id?.slice(-6)}
Product: ${job.productCode}
Customer: ${job.orderId?.leadId?.name || 'N/A'}
Phone: ${job.orderId?.primaryPhone || 'N/A'}
Company: ${job.orderId?.leadId?.company || 'N/A'}
Status: ${job.currentStatus?.replace('_', ' ')}
Priority: ${job.priority}
Quantity: ${job.originalQuantity}
Expected Dispatch: ${job.expectedDispatchDate ? moment(job.expectedDispatchDate).format('DD/MM/YYYY') : 'N/A'}`;
    
    navigator.clipboard.writeText(jobDetails).then(() => {
      message.success('Job details copied to clipboard!');
    }).catch(() => {
      message.error('Failed to copy job details');
    });
  };

  // Open WhatsApp with customer number
  const handleWhatsApp = (job) => {
    const phoneNumber = job.orderId?.primaryPhone || job.orderId?.leadId?.phone;
    if (phoneNumber) {
      const cleanedNumber = phoneNumber.replace(/\D/g, '');
      const whatsappUrl = `https://wa.me/91${cleanedNumber}`;
      window.open(whatsappUrl, '_blank');
    } else {
      message.error('No phone number available for this customer');
    }
  };

  // Professional Proforma Template Component (matching PerfomaInvoice.js design)
  const ProformaTemplate = ({ job, proformaData, onDownload, onCancel }) => {
    const proformaRef = useRef(null);

    // Convert number to words function
    const numberToWords = (num) => {
      const units = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine"];
      const teens = ["", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
      const tens = ["", "Ten", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];

      if (num === 0) return "Zero";

      const convertLessThanThousand = (n) => {
        if (n === 0) return "";
        let result = "";
        if (n >= 100) {
          result += units[Math.floor(n / 100)] + " Hundred ";
          n %= 100;
        }
        if (n >= 20) {
          result += tens[Math.floor(n / 10)] + " ";
          n %= 10;
        } else if (n >= 11 && n <= 19) {
          result += teens[n - 10] + " ";
          n = 0;
        }
        if (n > 0) {
          result += units[n] + " ";
        }
        return result;
      };

      let result = "";
      if (num >= 100000) {
        result += convertLessThanThousand(Math.floor(num / 100000)) + "Lakh ";
        num %= 100000;
      }
      if (num >= 1000) {
        result += convertLessThanThousand(Math.floor(num / 1000)) + "Thousand ";
        num %= 1000;
      }
      result += convertLessThanThousand(num);
      return result.trim();
    };

    // Calculate values
    const productSubtotal = job.originalQuantity * (proformaData.rate || 1);
    const additionalCharges = proformaData.additionalCharges || 0;
    const packagingCharges = proformaData.packagingCharges || 0;
    const totalBeforeGST = productSubtotal + additionalCharges + packagingCharges;
    const gstPercentage = parseFloat(proformaData.gstPercentage || '18');
    const gstAmount = (productSubtotal * gstPercentage) / 100;
    const cgstAmount = gstAmount / 2;
    const sgstAmount = gstAmount / 2;
    const totalAmount = totalBeforeGST + gstAmount;
    const roundedTotal = Math.round(totalAmount);
    const roundOff = roundedTotal - totalAmount;

    const handleDownloadPDF = async () => {
      if (!proformaRef.current) return;

      try {
        const canvas = await html2canvas(proformaRef.current, {
          scale: 1.5,
          logging: false,
          useCORS: true,
        });

        const imgData = canvas.toDataURL("image/png");
        const pdf = new jsPDF({
          orientation: "portrait",
          unit: "mm",
          format: "a4",
        });

        const imgWidth = 190;
        const pageHeight = 297;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        const scaleFactor = Math.min(1, pageHeight / imgHeight);
        const finalImgWidth = imgWidth * scaleFactor;
        const finalImgHeight = imgHeight * scaleFactor;

        pdf.addImage(imgData, "PNG", 10, 10, finalImgWidth, finalImgHeight);
        pdf.save(`proforma_${proformaData.proformaNumber}.pdf`);
        
        // Call onDownload callback
        onDownload();
      } catch (error) {
        console.error("Error generating PDF:", error);
        message.error("Failed to generate PDF");
      }
    };

    return (
      <div style={{ maxWidth: '900px', margin: '0 auto' }}>
        <div style={{ marginBottom: '20px', textAlign: 'right' }}>
          <Space>
            <Button onClick={onCancel}>Cancel</Button>
            <Button type="primary" onClick={handleDownloadPDF} icon={<DownloadOutlined />}>
              Download PDF
            </Button>
          </Space>
        </div>

        <div
          ref={proformaRef}
          style={{
            backgroundColor: 'white',
            padding: '24px',
            border: '1px solid #d9d9d9',
            fontFamily: 'Arial, sans-serif'
          }}
        >
          {/* Header */}
          <div style={{ textAlign: 'center', borderBottom: '2px solid #000', paddingBottom: '16px' }}>
            <h1 style={{ margin: '0', fontSize: '24px', fontWeight: 'bold' }}>PROFORMA INVOICE</h1>
            <p style={{ margin: '4px 0 0 0', fontSize: '14px' }}>(ORIGINAL FOR RECIPIENT)</p>
          </div>

          {/* Company and Customer Details */}
          <div style={{ marginTop: '8px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', fontSize: '12px' }}>
            <div>
              <h2 style={{ margin: '0 0 8px 0', fontWeight: 'bold' }}>Printverse Pvt. Ltd.</h2>
              <p style={{ margin: '2px 0' }}>20 JUNA TUKOGANJ</p>
              <p style={{ margin: '2px 0' }}>OPP. RAMBAGH PETROL PUMP</p>
              <p style={{ margin: '2px 0' }}>INDORE 452007</p>
              <p style={{ margin: '2px 0' }}>MOB 7389925002, 7036231237</p>
              <p style={{ margin: '2px 0' }}>GSTIN/UIN: 23AANCP1856B1ZM</p>
              <p style={{ margin: '2px 0' }}>State Name: Madhya Pradesh, Code: 23</p>
              <p style={{ margin: '2px 0' }}>E-Mail: accounts@printverse.com</p>
            </div>
            <div>
              <h3 style={{ margin: '0 0 8px 0', fontWeight: 'bold' }}>Buyer (Bill to)</h3>
              <p style={{ margin: '2px 0' }}>{job.orderId?.leadId?.name || proformaData.customerName || 'Customer'}</p>
              <p style={{ margin: '2px 0' }}>{job.orderId?.leadId?.company || ''}</p>
              <p style={{ margin: '2px 0' }}>
                {job.orderId?.flat || ''}{job.orderId?.flat && job.orderId?.area ? ', ' : ''}{job.orderId?.area || ''}
              </p>
              <p style={{ margin: '2px 0' }}>
                {job.orderId?.city || ''}{job.orderId?.city && job.orderId?.pincode ? ', ' : ''}{job.orderId?.pincode || ''}
              </p>
              <p style={{ margin: '2px 0' }}>{job.orderId?.primaryPhone || ''}</p>
              {job.orderId?.gstNumber && <p style={{ margin: '2px 0' }}>GSTIN: {job.orderId.gstNumber}</p>}
              {job.orderId?.state && <p style={{ margin: '2px 0' }}>State Name: {job.orderId.state}, Code: 23</p>}
            </div>
          </div>

          {/* Invoice Details */}
          <div style={{ marginTop: '16px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', borderTop: '1px solid #000', borderBottom: '1px solid #000', padding: '8px 0' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '14px' }}>
              <p style={{ margin: '2px 0', fontWeight: '600' }}>Proforma No.</p>
              <p style={{ margin: '2px 0' }}>{proformaData.proformaNumber}</p>
              <p style={{ margin: '2px 0', fontWeight: '600' }}>Dated</p>
              <p style={{ margin: '2px 0' }}>{proformaData.generatedAt}</p>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '14px' }}>
              <p style={{ margin: '2px 0', fontWeight: '600' }}>Order No.</p>
              <p style={{ margin: '2px 0' }}>{job.orderId?._id?.slice(-8) || 'N/A'}</p>
              <p style={{ margin: '2px 0', fontWeight: '600' }}>Destination</p>
              <p style={{ margin: '2px 0' }}>{job.orderId?.city || 'N/A'}</p>
            </div>
          </div>

          {/* Products Table */}
          <div style={{ marginTop: '16px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
              <thead>
                <tr style={{ backgroundColor: '#f5f5f5' }}>
                  <th style={{ border: '1px solid #000', padding: '4px', textAlign: 'left' }}>Sl No.</th>
                  <th style={{ border: '1px solid #000', padding: '4px', textAlign: 'left' }}>Description of Goods</th>
                  <th style={{ border: '1px solid #000', padding: '4px', textAlign: 'left' }}>HSN/SAC</th>
                  <th style={{ border: '1px solid #000', padding: '4px', textAlign: 'left' }}>GST</th>
                  <th style={{ border: '1px solid #000', padding: '4px', textAlign: 'left' }}>Quantity</th>
                  <th style={{ border: '1px solid #000', padding: '4px', textAlign: 'left' }}>Rate</th>
                  <th style={{ border: '1px solid #000', padding: '4px', textAlign: 'left' }}>per</th>
                  <th style={{ border: '1px solid #000', padding: '4px', textAlign: 'left' }}>Amount</th>
                </tr>
              </thead>
              <tbody>
                {/* Main Product */}
                <tr>
                  <td style={{ border: '1px solid #000', padding: '4px' }}>1</td>
                  <td style={{ border: '1px solid #000', padding: '4px' }}>{job.productCode || 'Product'}</td>
                  <td style={{ border: '1px solid #000', padding: '4px' }}>3920</td>
                  <td style={{ border: '1px solid #000', padding: '4px' }}>{gstPercentage}%</td>
                  <td style={{ border: '1px solid #000', padding: '4px' }}>{job.originalQuantity} pcs</td>
                  <td style={{ border: '1px solid #000', padding: '4px' }}>{(proformaData.rate || 1).toFixed(2)}</td>
                  <td style={{ border: '1px solid #000', padding: '4px' }}>pcs</td>
                  <td style={{ border: '1px solid #000', padding: '4px' }}>{productSubtotal.toFixed(2)}</td>
                </tr>

                {/* Additional Charges */}
                {additionalCharges > 0 && (
                  <tr>
                    <td style={{ border: '1px solid #000', padding: '4px' }}>2</td>
                    <td style={{ border: '1px solid #000', padding: '4px' }}>Additional Charges</td>
                    <td style={{ border: '1px solid #000', padding: '4px' }}>9997</td>
                    <td style={{ border: '1px solid #000', padding: '4px' }}>0%</td>
                    <td style={{ border: '1px solid #000', padding: '4px' }}>1</td>
                    <td style={{ border: '1px solid #000', padding: '4px' }}>{additionalCharges.toFixed(2)}</td>
                    <td style={{ border: '1px solid #000', padding: '4px' }}>-</td>
                    <td style={{ border: '1px solid #000', padding: '4px' }}>{additionalCharges.toFixed(2)}</td>
                  </tr>
                )}

                {/* Packaging Charges */}
                {packagingCharges > 0 && (
                  <tr>
                    <td style={{ border: '1px solid #000', padding: '4px' }}>{additionalCharges > 0 ? '3' : '2'}</td>
                    <td style={{ border: '1px solid #000', padding: '4px' }}>Packaging Charges</td>
                    <td style={{ border: '1px solid #000', padding: '4px' }}>9997</td>
                    <td style={{ border: '1px solid #000', padding: '4px' }}>0%</td>
                    <td style={{ border: '1px solid #000', padding: '4px' }}>1</td>
                    <td style={{ border: '1px solid #000', padding: '4px' }}>{packagingCharges.toFixed(2)}</td>
                    <td style={{ border: '1px solid #000', padding: '4px' }}>-</td>
                    <td style={{ border: '1px solid #000', padding: '4px' }}>{packagingCharges.toFixed(2)}</td>
                  </tr>
                )}

                {/* Subtotal */}
                <tr>
                  <td colSpan={7} style={{ border: '1px solid #000', padding: '4px', textAlign: 'right', fontWeight: 'bold' }}>
                    Subtotal
                  </td>
                  <td style={{ border: '1px solid #000', padding: '4px' }}>{totalBeforeGST.toFixed(2)}</td>
                </tr>

                {/* GST */}
                <tr>
                  <td colSpan={7} style={{ border: '1px solid #000', padding: '4px', textAlign: 'right', fontWeight: 'bold' }}>
                    CGST
                  </td>
                  <td style={{ border: '1px solid #000', padding: '4px' }}>{cgstAmount.toFixed(2)}</td>
                </tr>
                <tr>
                  <td colSpan={7} style={{ border: '1px solid #000', padding: '4px', textAlign: 'right', fontWeight: 'bold' }}>
                    SGST
                  </td>
                  <td style={{ border: '1px solid #000', padding: '4px' }}>{sgstAmount.toFixed(2)}</td>
                </tr>

                {/* Round Off */}
                <tr>
                  <td colSpan={7} style={{ border: '1px solid #000', padding: '4px', textAlign: 'right', fontWeight: 'bold' }}>
                    Round Off
                  </td>
                  <td style={{ border: '1px solid #000', padding: '4px' }}>{roundOff.toFixed(2)}</td>
                </tr>

                {/* Total */}
                <tr style={{ backgroundColor: '#f5f5f5' }}>
                  <td colSpan={7} style={{ border: '1px solid #000', padding: '4px', textAlign: 'right', fontWeight: 'bold' }}>
                    Total
                  </td>
                  <td style={{ border: '1px solid #000', padding: '4px', fontWeight: 'bold' }}>
                    ₹ {roundedTotal.toFixed(2)}
                  </td>
                </tr>

                {/* Advance and Balance */}
                {proformaData.advanceAmount > 0 && (
                  <>
                    <tr>
                      <td colSpan={7} style={{ border: '1px solid #000', padding: '4px', textAlign: 'right', fontWeight: 'bold' }}>
                        Advance Amount
                      </td>
                      <td style={{ border: '1px solid #000', padding: '4px' }}>₹ {proformaData.advanceAmount.toFixed(2)}</td>
                    </tr>
                    <tr>
                      <td colSpan={7} style={{ border: '1px solid #000', padding: '4px', textAlign: 'right', fontWeight: 'bold' }}>
                        Balance Amount
                      </td>
                      <td style={{ border: '1px solid #000', padding: '4px' }}>
                        ₹ {(roundedTotal - proformaData.advanceAmount).toFixed(2)}
                      </td>
                    </tr>
                  </>
                )}
              </tbody>
            </table>
          </div>

          {/* Amount in Words */}
          <div style={{ marginTop: '16px', border: '1px solid #000', padding: '8px', fontSize: '12px' }}>
            <p style={{ margin: 0 }}>
              <span style={{ fontWeight: 'bold' }}>Amount Chargeable (in words):</span> INR{' '}
              {numberToWords(roundedTotal)} Only
            </p>
          </div>

          {/* GST Summary */}
          <div style={{ marginTop: '16px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
              <thead>
                <tr style={{ backgroundColor: '#f5f5f5' }}>
                  <th style={{ border: '1px solid #000', padding: '4px', textAlign: 'left' }}>HSN/SAC</th>
                  <th style={{ border: '1px solid #000', padding: '4px', textAlign: 'left' }}>Taxable Value</th>
                  <th style={{ border: '1px solid #000', padding: '4px', textAlign: 'left' }}>Central Tax</th>
                  <th style={{ border: '1px solid #000', padding: '4px', textAlign: 'left' }}>State Tax</th>
                  <th style={{ border: '1px solid #000', padding: '4px', textAlign: 'left' }}>Total Tax Amount</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style={{ border: '1px solid #000', padding: '4px' }}>3920</td>
                  <td style={{ border: '1px solid #000', padding: '4px' }}>{productSubtotal.toFixed(2)}</td>
                  <td style={{ border: '1px solid #000', padding: '4px' }}>
                    {cgstAmount.toFixed(2)} @ {gstPercentage / 2}%
                  </td>
                  <td style={{ border: '1px solid #000', padding: '4px' }}>
                    {sgstAmount.toFixed(2)} @ {gstPercentage / 2}%
                  </td>
                  <td style={{ border: '1px solid #000', padding: '4px' }}>{gstAmount.toFixed(2)}</td>
                </tr>
                <tr style={{ backgroundColor: '#f5f5f5' }}>
                  <td style={{ border: '1px solid #000', padding: '4px', fontWeight: 'bold' }}>Total</td>
                  <td style={{ border: '1px solid #000', padding: '4px' }}>{productSubtotal.toFixed(2)}</td>
                  <td style={{ border: '1px solid #000', padding: '4px' }}>{cgstAmount.toFixed(2)}</td>
                  <td style={{ border: '1px solid #000', padding: '4px' }}>{sgstAmount.toFixed(2)}</td>
                  <td style={{ border: '1px solid #000', padding: '4px' }}>{gstAmount.toFixed(2)}</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Footer */}
          <div style={{ marginTop: '16px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
            <div style={{ border: '1px solid #000', padding: '8px' }}>
              <h3 style={{ margin: '0 0 8px 0', fontWeight: 'bold', fontSize: '12px' }}>Declaration</h3>
              <p style={{ margin: 0, fontSize: '10px' }}>
                We declare that this proforma shows the actual price of the goods described and that all particulars are
                true and correct.
              </p>
            </div>
            <div style={{ border: '1px solid #000', padding: '8px' }}>
              <h3 style={{ margin: '0 0 8px 0', fontWeight: 'bold', fontSize: '12px' }}>Company's Bank Details</h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px', fontSize: '10px' }}>
                <p style={{ margin: 0 }}>Bank Name:</p>
                <p style={{ margin: 0 }}>ICICI</p>
                <p style={{ margin: 0 }}>A/c No.:</p>
                <p style={{ margin: 0 }}>657305602279</p>
                <p style={{ margin: 0 }}>Branch & IFS Code:</p>
                <p style={{ margin: 0 }}>ICIC0006573</p>
              </div>
            </div>
          </div>

          {/* Signature */}
          <div style={{ marginTop: '16px', textAlign: 'right' }}>
            <p style={{ margin: '0 0 24px 0', fontWeight: 'bold', fontSize: '12px' }}>for Printverse Pvt Ltd</p>
            <p style={{ margin: 0, fontSize: '12px' }}>Authorised Signatory</p>
          </div>

          {/* Computer Generated */}
          <div style={{ marginTop: '8px', textAlign: 'center', fontSize: '10px' }}>
            <p style={{ margin: 0 }}>This is a Computer Generated Proforma</p>
          </div>
        </div>
      </div>
    );
  };

  const handleGenerateProforma = async (job) => {
    try {
      // Get the actual user ObjectId from Firebase UID  
      const userResponse = await axios.get(`${constants.DOMAIN}/api/v1/systemuser/getUser?firebaseId=${currentUser._delegate.uid}`);
      const userId = userResponse.data.data.users[0]?._id;
      const userName = userResponse.data.data.users[0]?.name;
      
      const timestamp = new Date();
      
      // Calculate rate from existing order data (approximation)
      const finalAmount = getDecimalValue(job.orderId?.finalAmount);
      const additionalCharges = getDecimalValue(job.orderId?.additionalCharges);
      const packagingCharges = getDecimalValue(job.orderId?.packagingCharges);
      const amountWithoutGST = getDecimalValue(job.orderId?.amountWithoutGST);
      
      // Estimate product rate (remove additional and packaging charges from total)
      const productAmount = amountWithoutGST - additionalCharges - packagingCharges;
      const estimatedRate = job.originalQuantity > 0 ? (productAmount / job.originalQuantity) : 1;
      
      // Create proforma data for frontend display
      const proformaData = {
        jobId: job._id,
        orderId: job.orderId?._id,
        customerPhone: job.orderId?.primaryPhone,
        customerName: job.orderId?.leadId?.name,
        finalAmount: finalAmount,
        advanceAmount: getDecimalValue(job.orderId?.advanceAmount),
        balanceAmount: finalAmount - getDecimalValue(job.orderId?.advanceAmount),
        amountWithGST: getDecimalValue(job.orderId?.amountWithGST),
        amountWithoutGST: amountWithoutGST,
        gstPercentage: job.orderId?.gstPercentage || '18',
        additionalCharges: additionalCharges,
        packagingCharges: packagingCharges,
        rate: estimatedRate > 0 ? estimatedRate : 1, // Ensure positive rate
        generatedBy: userName,
        generatedAt: timestamp.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }),
        proformaNumber: `PI-${job._id?.slice(-6)}-${timestamp.getTime().toString().slice(-6)}`
      };

      // Show professional proforma template
      Modal.confirm({
        title: null,
        icon: null,
        width: 1000,
        content: (
          <ProformaTemplate 
            job={job} 
            proformaData={proformaData}
            onDownload={async () => {
              try {
                // Update job status after PDF is downloaded
                const updateData = {
                  status: 'PI_GENERATED',
                  proformaGenerated: true,
                  proformaUrl: `proforma_${proformaData.proformaNumber}.pdf`,
                  comments: `Proforma ${proformaData.proformaNumber} generated and downloaded on ${proformaData.generatedAt} by ${userName}`,
                  userId: userId
                };
                
                await axios.patch(`${constants.DOMAIN}/api/v1/jobs/accounts/${job._id}`, updateData);
                message.success('Proforma generated, downloaded and status updated!');
                
                // Refresh the jobs list
                if (activeTab === 'accounts') {
                  fetchJobsByDepartment('accounts');
                } else {
                  fetchAllJobs();
                }
                Modal.destroyAll();
              } catch (error) {
                console.error('Error updating job status:', error);
                message.error('PDF downloaded but failed to update job status');
                Modal.destroyAll();
              }
            }}
            onCancel={() => Modal.destroyAll()}
          />
        ),
        footer: null,
        centered: true,
        maskClosable: false,
      });
      
    } catch (error) {
      message.error('Failed to generate proforma');
      console.error('Error:', error);
    }
  };

  const applyFilters = () => {
    if (activeTab !== 'dashboard' && activeTab !== 'order-jobs') {
      fetchJobsByDepartment(activeTab);
    } else if (activeTab === 'order-jobs' && filterByOrderId) {
      fetchJobsByOrderId(filterByOrderId);
    } else {
      fetchAllJobs();
    }
  };

  const clearFilters = () => {
    setFilters({});
    // Refresh data after clearing filters
    if (activeTab !== 'dashboard' && activeTab !== 'order-jobs') {
      fetchJobsByDepartment(activeTab);
    } else if (activeTab === 'order-jobs' && filterByOrderId) {
      fetchJobsByOrderId(filterByOrderId);
    } else {
      fetchAllJobs();
    }
  };

  const FilterComponent = () => (
    <Card size="small" className="mb-4">
      <Row gutter={16} className="mb-3">
        <Col span={4}>
          <Input 
            placeholder="Job ID" 
            value={filters.jobId || ''}
            onChange={(e) => setFilters({...filters, jobId: e.target.value})}
          />
        </Col>
        <Col span={4}>
          <Input 
            placeholder="Product Code" 
            value={filters.productCode || ''}
            onChange={(e) => setFilters({...filters, productCode: e.target.value})}
          />
        </Col>
        <Col span={4}>
          <Input 
            placeholder="Customer Name" 
            value={filters.customerName || ''}
            onChange={(e) => setFilters({...filters, customerName: e.target.value})}
          />
        </Col>
        <Col span={4}>
          <Input 
            placeholder="Company Name" 
            value={filters.companyName || ''}
            onChange={(e) => setFilters({...filters, companyName: e.target.value})}
          />
        </Col>
        <Col span={4}>
          <Input 
            placeholder="Customer Phone" 
            value={filters.customerPhone || ''}
            onChange={(e) => setFilters({...filters, customerPhone: e.target.value})}
          />
        </Col>
        <Col span={4}>
          <Select 
            placeholder="Status" 
            style={{ width: '100%' }}
            value={filters.status}
            onChange={(value) => setFilters({...filters, status: value})}
            allowClear
          >
            <Option value="PENDING_DESIGN">Pending Design</Option>
            <Option value="PENDING_PRODUCTION">Pending Production</Option>
            <Option value="PENDING_DISPATCH">Pending Dispatch</Option>
            <Option value="DISPATCHED">Dispatched</Option>
            <Option value="DELIVERED">Delivered</Option>
          </Select>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={4}>
          <Select 
            placeholder="Priority" 
            style={{ width: '100%' }}
            value={filters.priority}
            onChange={(value) => setFilters({...filters, priority: value})}
            allowClear
          >
            <Option value="HIGH">High</Option>
            <Option value="MEDIUM">Medium</Option>
            <Option value="LOW">Low</Option>
          </Select>
        </Col>
        <Col span={4}>
          <DatePicker 
            placeholder="From Date" 
            style={{ width: '100%' }}
            value={filters.fromDate ? moment(filters.fromDate) : null}
            onChange={(date) => setFilters({...filters, fromDate: date ? date.format('YYYY-MM-DD') : null})}
          />
        </Col>
        <Col span={4}>
          <DatePicker 
            placeholder="To Date" 
            style={{ width: '100%' }}
            value={filters.toDate ? moment(filters.toDate) : null}
            onChange={(date) => setFilters({...filters, toDate: date ? date.format('YYYY-MM-DD') : null})}
          />
        </Col>
        <Col span={12}>
          <Space>
            <Button icon={<FilterOutlined />} onClick={applyFilters}>
              Apply Filters
            </Button>
            <Button onClick={clearFilters}>Clear All</Button>
          </Space>
        </Col>
      </Row>
    </Card>
  );

  // Dashboard Statistics Component
  const DashboardStats = () => (
    <div className="mb-6">
      <Row gutter={16} className="mb-6">
        <Col span={4}>
          <Card>
            <Statistic
              title="Total Jobs"
              value={jobs.length}
              prefix={<FileTextOutlined />}
            />
          </Card>
        </Col>
        <Col span={4}>
          <Card>
            <Statistic
              title="Urgent Jobs"
              value={statistics.urgentJobs || 0}
              prefix={<ExclamationCircleOutlined />}
              valueStyle={{ color: '#cf1322' }}
            />
          </Card>
        </Col>
        <Col span={4}>
          <Card>
            <Statistic
              title="Design Pending"
              value={statistics.statusStats?.find(s => s._id === 'PENDING_DESIGN')?.count || 0}
              prefix={<BgColorsOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col span={4}>
          <Card>
            <Statistic
              title="Production"
              value={statistics.statusStats?.find(s => s._id === 'PENDING_PRODUCTION')?.count || 0}
              prefix={<ToolOutlined />}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
        <Col span={4}>
          <Card>
            <Statistic
              title="Dispatch"
              value={statistics.statusStats?.find(s => s._id === 'PENDING_DISPATCH')?.count || 0}
              prefix={<CarOutlined />}
              valueStyle={{ color: '#13c2c2' }}
            />
          </Card>
        </Col>
        <Col span={4}>
          <Card>
            <Statistic
              title="Completed"
              value={statistics.statusStats?.find(s => s._id === 'COMPLETED')?.count || 0}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );

  // Design Department Columns
  const designColumns = [
    {
      title: 'Job ID',
      dataIndex: '_id',
      key: '_id',
      render: (id) => <Text code>{id.slice(-6)}</Text>,
      width: 80,
    },
    {
      title: 'Product Details',
      key: 'productDetails',
      render: (_, record) => (
        <div>
          <div><strong>{record.productCode}</strong></div>
          <div className="text-gray-500 text-xs">{record.productName}</div>
          <div className="text-xs text-blue-600">#{record.orderId?._id?.slice(-6)}</div>
          <div className="text-xs">Qty: {record.originalQuantity}</div>
        </div>
      ),
      width: 150,
    },
    {
      title: 'Client Details',
      key: 'clientDetails',
      render: (_, record) => (
        <div>
          <div className="font-semibold text-green-700">{record.orderId?.leadId?.name || 'N/A'}</div>
          <div className="text-xs text-purple-600">{record.orderId?.leadId?.company || 'No Company'}</div>
          <div className="text-xs flex items-center">
            <PhoneOutlined className="mr-1" />
            {record.orderId?.primaryPhone || 'No Phone'}
          </div>
          <div className="text-xs text-gray-500">{record.orderId?.leadId?.city || record.orderId?.city || 'N/A'}</div>
        </div>
      ),
      width: 180,
    },
    {
      title: 'Priority',
      dataIndex: 'priority',
      key: 'priority',
      render: (priority) => (
        <Tag color={getPriorityColor(priority)}>{priority}</Tag>
      ),
      width: 100,
    },
    {
      title: 'Expected Dispatch',
      dataIndex: 'expectedDispatchDate',
      key: 'expectedDispatchDate',
      render: (date, record) => (
        <div>
          <div>{date ? moment(date).format('DD/MM/YYYY') : 'N/A'}</div>
          {record.isUrgent && <Tag color="red" size="small">URGENT</Tag>}
        </div>
      ),
      width: 120,
    },
    {
      title: 'Design Status',
      dataIndex: ['design', 'status'],
      key: 'designStatus',
      render: (status) => <Tag color={status === 'COMPLETED' ? 'green' : 'blue'}>{status || 'PENDING'}</Tag>,
      width: 120,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Update Design">
            <Button
              icon={<EditOutlined />}
              size="small"
              onClick={() => openJobDrawer(record, 'design')}
              disabled={(!isAdmin && !isDesign) || record.currentStatus !== 'PENDING_DESIGN'}
            />
          </Tooltip>
          <Tooltip title="View Job">
            <Button
              icon={<EyeOutlined />}
              size="small"
              onClick={() => handleViewJob(record)}
            />
          </Tooltip>
          <Tooltip title="Copy Details">
            <Button
              icon={<CopyOutlined />}
              size="small"
              onClick={() => handleCopyJob(record)}
            />
          </Tooltip>
          <Tooltip title="WhatsApp Customer">
            <Button
              icon={<WhatsAppOutlined />}
              size="small"
              style={{ color: '#25D366' }}
              onClick={() => handleWhatsApp(record)}
              disabled={!record.orderId?.primaryPhone}
            />
          </Tooltip>
          <Tooltip title="Print Summary">
            <Button
              icon={<FileTextOutlined />}
              size="small"
              onClick={() => handlePrintSummary(record)}
            />
          </Tooltip>
        </Space>
      ),
      width: 140,
      fixed: 'right',
    },
  ];

  // Accounts Department Columns
  const accountsColumns = [
    {
      title: 'Job ID',
      dataIndex: '_id',
      key: '_id',
      render: (id) => <Text code>{id.slice(-6)}</Text>,
      width: 100,
    },
    {
      title: 'Product & Client',
      key: 'productClient',
      render: (_, record) => (
        <div>
          <div><strong>{record.productCode}</strong></div>
          <div className="text-gray-500 text-xs">{record.productName}</div>
          <div className="text-xs text-blue-600">#{record.orderId?._id?.slice(-6)}</div>
          <div className="text-xs font-semibold text-green-700">{record.orderId?.leadId?.name || 'N/A'}</div>
          <div className="text-xs text-purple-600">{record.orderId?.leadId?.company || 'No Company'}</div>
          <div className="text-xs"><UserOutlined /> {record.orderId?.primaryPhone}</div>
        </div>
      ),
      width: 200,
    },
    {
      title: 'Financial Details',
      key: 'financialInfo',
      render: (_, record) => (
        <div>
          <div className="text-xs"><strong>Final:</strong> ₹{getDecimalValue(record.orderId?.finalAmount)?.toLocaleString('en-IN') || 'N/A'}</div>
          <div className="text-xs"><strong>Advance:</strong> ₹{getDecimalValue(record.orderId?.advanceAmount)?.toLocaleString('en-IN') || '0'}</div>
          <div className="text-xs"><strong>Balance:</strong> ₹{
            (getDecimalValue(record.orderId?.finalAmount) - getDecimalValue(record.orderId?.advanceAmount))?.toLocaleString('en-IN') || 'N/A'
          }</div>
          <div className="text-xs">
            <Tag size="small" color={
              (!getDecimalValue(record.orderId?.finalAmount)) ? 'default' :
              (getDecimalValue(record.orderId?.advanceAmount) >= getDecimalValue(record.orderId?.finalAmount)) ? 'green' :
              (getDecimalValue(record.orderId?.advanceAmount) > 0) ? 'orange' : 'red'
            }>
              {(!getDecimalValue(record.orderId?.finalAmount)) ? 'No Amount' :
               (getDecimalValue(record.orderId?.advanceAmount) >= getDecimalValue(record.orderId?.finalAmount)) ? 'Paid' :
               (getDecimalValue(record.orderId?.advanceAmount) > 0) ? 'Partial' : 'Unpaid'}
            </Tag>
          </div>
        </div>
      ),
      width: 150,
    },
    {
      title: 'Quantity & GST',
      key: 'quantityGst',
      render: (_, record) => (
        <div>
          <div className="text-xs">Qty: {record.originalQuantity}</div>
          <div className="text-xs">GST: {record.orderId?.gstPercentage || '18'}%</div>
          <div className="text-xs">w/GST: ₹{getDecimalValue(record.orderId?.amountWithGST)?.toLocaleString('en-IN') || 'N/A'}</div>
          <div className="text-xs">w/o GST: ₹{getDecimalValue(record.orderId?.amountWithoutGST)?.toLocaleString('en-IN') || 'N/A'}</div>
          {getDecimalValue(record.orderId?.additionalCharges) > 0 && (
            <div className="text-xs">Extra: ₹{getDecimalValue(record.orderId.additionalCharges)?.toLocaleString('en-IN')}</div>
          )}
        </div>
      ),
      width: 120,
    },
    {
      title: 'Reconciliation',
      dataIndex: ['accounts', 'status'],
      key: 'accountsStatus',
      render: (status) => {
        const colors = {
          'UNRECONCILED': 'red',
          'RECONCILED': 'orange',
          'PI_GENERATED': 'blue',
          'PENDING_PAYMENT': 'purple',
          'COMPLETED': 'green'
        };
        return <Tag color={colors[status] || 'red'}>{status?.replace('_', ' ') || 'UNRECONCILED'}</Tag>;
      },
      width: 120,
    },
    {
      title: 'Payment Status',
      dataIndex: ['accounts', 'paymentStatus'],
      key: 'paymentStatus',
      render: (status) => <Tag color={status === 'COMPLETED' ? 'green' : 'orange'}>{status || 'PENDING'}</Tag>,
      width: 100,
    },
    {
      title: 'Proforma',
      key: 'proforma',
      render: (_, record) => (
        <div>
          <Tag color={record.accounts?.proformaGenerated ? 'green' : 'red'} size="small">
            {record.accounts?.proformaGenerated ? 'Generated' : 'Pending'}
          </Tag>
          {/* {record.accounts?.proformaUrl && (
            <div>
              <Button 
                size="small" 
                type="link" 
                icon={<DownloadOutlined />}
                onClick={() => window.open(record.accounts.proformaUrl, '_blank')}
              >
                Download
              </Button>
            </div>
          )}
          {record.accounts?.proformaGeneratedAt && (
            <div className="text-xs text-gray-500">
              {moment(record.accounts.proformaGeneratedAt).format('DD/MM')}
            </div>
          )} */}
        </div>
      ),
      width: 120,
    },
    {
      title: 'Priority',
      dataIndex: 'priority',
      key: 'priority', 
      render: (priority) => <Tag color={getPriorityColor(priority)} size="small">{priority}</Tag>,
      width: 80,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space direction="vertical" size="small">
          <Button
            icon={<EditOutlined />}
            size="small"
            onClick={() => openJobDrawer(record, 'accounts')}
            disabled={!isAdmin && !isAccounts}
          >
            Update
          </Button>
          <Button
            icon={<DollarOutlined />}
            size="small"
            onClick={() => handleGenerateProforma(record)}
            disabled={!isAdmin && !isAccounts}
          >
            {record.accounts?.proformaGenerated ? 'Re-Generate' : 'Generate'}
          </Button>
          <Button
            icon={<EyeOutlined />}
            size="small"
            onClick={() => handleViewJob(record)}
          >
            View
          </Button>
        </Space>
      ),
      width: 120,
    },
  ];

  // Production Department Columns
  const productionColumns = [
    {
      title: 'Job ID',
      dataIndex: '_id',
      key: '_id',
      render: (id) => <Text code>{id.slice(-6)}</Text>,
      width: 80,
    },
    {
      title: 'Product Details',
      key: 'productDetails',
      render: (_, record) => (
        <div>
          <div><strong>{record.productCode}</strong></div>
          <div className="text-gray-500 text-xs">{record.productName}</div>
          <div className="text-xs text-blue-600">#{record.orderId?._id?.slice(-6)}</div>
          <div className="text-xs">Original: {record.originalQuantity}</div>
        </div>
      ),
      width: 150,
    },
    {
      title: 'Client Details',
      key: 'clientDetails',
      render: (_, record) => (
        <div>
          <div className="font-semibold text-green-700">{record.orderId?.leadId?.name || 'N/A'}</div>
          <div className="text-xs text-purple-600">{record.orderId?.leadId?.company || 'No Company'}</div>
          <div className="text-xs flex items-center">
            <PhoneOutlined className="mr-1" />
            {record.orderId?.primaryPhone || 'No Phone'}
          </div>
          <div className="text-xs text-gray-500">{record.orderId?.leadId?.city || record.orderId?.city || 'N/A'}</div>
        </div>
      ),
      width: 180,
    },
    {
      title: 'Quantities',
      key: 'quantities',
      render: (_, record) => (
        <div>
          <div>Output: {record.production?.outputQuantity || 'N/A'}</div>
          <div>Damage: {record.production?.damageQuantity || 0}</div>
        </div>
      ),
      width: 100,
    },
    {
      title: 'Priority',
      dataIndex: 'priority',
      key: 'priority',
      render: (priority) => <Tag color={getPriorityColor(priority)}>{priority}</Tag>,
      width: 100,
    },
    {
      title: 'Production Status',
      dataIndex: ['production', 'status'],
      key: 'productionStatus',
      render: (status) => {
        const colors = { 'PENDING': 'blue', 'IN_PROGRESS': 'orange', 'COMPLETED': 'green' };
        return <Tag color={colors[status] || 'blue'}>{status?.replace('_', ' ') || 'PENDING'}</Tag>;
      },
      width: 120,
    },
    {
      title: 'Comments',
      dataIndex: ['production', 'comments'],
      key: 'comments',
      render: (comments) => comments ? <Text ellipsis style={{ maxWidth: 150 }}>{comments}</Text> : 'N/A',
      width: 150,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Update Production">
            <Button
              icon={<EditOutlined />}
              size="small"
              onClick={() => openJobDrawer(record, 'production')}
              disabled={(!isAdmin && !isProduction) || record.currentStatus !== 'PENDING_PRODUCTION'}
            />
          </Tooltip>
          <Tooltip title="View Job">
            <Button
              icon={<EyeOutlined />}
              size="small"
              onClick={() => handleViewJob(record)}
            />
          </Tooltip>
          <Tooltip title="Copy Details">
            <Button
              icon={<CopyOutlined />}
              size="small"
              onClick={() => handleCopyJob(record)}
            />
          </Tooltip>
          <Tooltip title="WhatsApp Customer">
            <Button
              icon={<WhatsAppOutlined />}
              size="small"
              style={{ color: '#25D366' }}
              onClick={() => handleWhatsApp(record)}
              disabled={!record.orderId?.primaryPhone}
            />
          </Tooltip>
        </Space>
      ),
      width: 140,
      fixed: 'right',
    },
  ];

  // Dispatch Department Columns
  const dispatchColumns = [
    {
      title: 'Job ID',
      dataIndex: '_id',
      key: '_id',
      render: (id) => <Text code>{id.slice(-6)}</Text>,
      width: 80,
    },
    {
      title: 'Product Details',
      key: 'productDetails',
      render: (_, record) => (
        <div>
          <div><strong>{record.productCode}</strong></div>
          <div className="text-gray-500 text-xs">{record.productName}</div>
          <div className="text-xs text-blue-600">#{record.orderId?._id?.slice(-6)}</div>
          <div className="text-xs">Original: {record.originalQuantity}</div>
        </div>
      ),
      width: 150,
    },
    {
      title: 'Client Details',
      key: 'clientDetails',
      render: (_, record) => (
        <div>
          <div className="font-semibold text-green-700">{record.orderId?.leadId?.name || 'N/A'}</div>
          <div className="text-xs text-purple-600">{record.orderId?.leadId?.company || 'No Company'}</div>
          <div className="text-xs flex items-center">
            <PhoneOutlined className="mr-1" />
            {record.orderId?.primaryPhone || 'No Phone'}
          </div>
          <div className="text-xs text-gray-500">{record.orderId?.leadId?.city || record.orderId?.city || 'N/A'}</div>
        </div>
      ),
      width: 180,
    },
    {
      title: 'Final Qty',
      dataIndex: ['dispatch', 'finalQuantity'],
      key: 'finalQuantity',
      render: (qty) => qty || 'N/A',
      width: 80,
    },
    {
      title: 'Damage',
      dataIndex: ['dispatch', 'finalDamage'],
      key: 'finalDamage',
      render: (damage) => damage || 0,
      width: 80,
    },
    {
      title: 'Dispatch Mode',
      dataIndex: ['dispatch', 'dispatchMode'],
      key: 'dispatchMode',
      render: (mode) => mode ? <Tag>{mode}</Tag> : 'N/A',
      width: 100,
    },
    {
      title: 'Weight/Boxes',
      key: 'weightBoxes',
      render: (_, record) => (
        <div>
          <div>Weight: {record.dispatch?.weight || 'N/A'} kg</div>
          <div>Boxes: {record.dispatch?.numberOfBoxes || 'N/A'}</div>
        </div>
      ),
      width: 120,
    },
    {
      title: 'Status',
      dataIndex: ['dispatch', 'status'],
      key: 'dispatchStatus',
      render: (status) => {
        const colors = {
          'PENDING': 'blue',
          'READY_TO_DISPATCH': 'orange',
          'DISPATCHED': 'green',
          'DELIVERED': 'success'
        };
        return <Tag color={colors[status]}>{status?.replace('_', ' ')}</Tag>;
      },
      width: 120,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Update Dispatch">
            <Button
              icon={<EditOutlined />}
              size="small"
              onClick={() => openJobDrawer(record, 'dispatch')}
              disabled={record.currentStatus !== 'PENDING_DISPATCH'}
            />
          </Tooltip>
          <Tooltip title="View Job">
            <Button
              icon={<EyeOutlined />}
              size="small"
              onClick={() => handleViewJob(record)}
            />
          </Tooltip>
          <Tooltip title="Copy Details">
            <Button
              icon={<CopyOutlined />}
              size="small"
              onClick={() => handleCopyJob(record)}
            />
          </Tooltip>
          <Tooltip title="WhatsApp Customer">
            <Button
              icon={<WhatsAppOutlined />}
              size="small"
              style={{ color: '#25D366' }}
              onClick={() => handleWhatsApp(record)}
              disabled={!record.orderId?.primaryPhone}
            />
          </Tooltip>
        </Space>
      ),
      width: 140,
      fixed: 'right',
    },
  ];

  const renderUpdateForm = () => {
    if (!selectedJob) return null;

    const { department } = selectedJob;

    switch (department) {
      case 'design':
        return (
          <>
            <Form.Item
              name="outputQuantity"
              label="Output Quantity"
              rules={[{ required: true, message: 'Please enter output quantity' }]}
            >
              <InputNumber min={1} className="w-full" />
            </Form.Item>
            
            <Form.Item name="designScreenshot" label="Design Screenshot URL">
              <Input placeholder="Enter screenshot URL or upload" />
            </Form.Item>
            
            <Form.Item name="instructions" label="Design Instructions">
              <TextArea rows={3} placeholder="Enter design instructions" />
            </Form.Item>
            
            <Form.Item name="cuttingFileName" label="Cutting File Name">
              <Input placeholder="Enter cutting file name" />
            </Form.Item>
            
            <Form.Item name="printingFileName" label="Printing File Name">
              <Input placeholder="Enter printing file name" />
            </Form.Item>

            <Form.Item name="comments" label="Design Comments">
              <TextArea rows={3} placeholder="Add any design comments or notes" />
            </Form.Item>

            {/* File Upload Section */}
            <Form.Item label="Upload Design Files">
              <div style={{ border: '2px dashed #d9d9d9', borderRadius: '6px', padding: '20px', textAlign: 'center' }}>
                <input
                  type="file"
                  multiple
                  onChange={handleFileSelection}
                  style={{ display: 'none' }}
                  id="design-file-upload"
                />
                <label htmlFor="design-file-upload" style={{ cursor: 'pointer' }}>
                  <UploadOutlined style={{ fontSize: '24px', color: '#1890ff', marginBottom: '8px' }} />
                  <div>Click to select files or drag and drop</div>
                  <div style={{ fontSize: '12px', color: '#999' }}>Support for multiple files</div>
                </label>
              </div>
              {selectedFiles.length > 0 && (
                <div style={{ marginTop: '12px' }}>
                  <Text strong>Selected Files:</Text>
                  {selectedFiles.map((file, index) => (
                    <div key={index} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0' }}>
                      <span>{file.name}</span>
                      <Button size="small" danger onClick={() => removeSelectedFile(index)}>Remove</Button>
                    </div>
                  ))}
                </div>
              )}
            </Form.Item>
          </>
        );

      case 'accounts':
        return (
          <>
            <Form.Item
              name="status"
              label="Reconciliation Status"
              rules={[{ required: true, message: 'Please select status' }]}
            >
              <Select>
                <Option value="UNRECONCILED">Unreconciled</Option>
                <Option value="RECONCILED">Reconciled</Option>
                <Option value="PI_GENERATED">PI Generated</Option>
                <Option value="PENDING_PAYMENT">Pending Payment</Option>
                <Option value="COMPLETED">Completed</Option>
              </Select>
            </Form.Item>
            
            <Form.Item name="proformaGenerated" label="Proforma Generated">
              <Select>
                <Option value={true}>Yes</Option>
                <Option value={false}>No</Option>
              </Select>
            </Form.Item>
            
            <Form.Item name="proformaUrl" label="Proforma URL">
              <Input placeholder="Enter proforma document URL" />
            </Form.Item>
            
            <Form.Item name="paymentStatus" label="Payment Status">
              <Select>
                <Option value="PENDING">Pending</Option>
                <Option value="PARTIAL">Partial</Option>
                <Option value="COMPLETED">Completed</Option>
              </Select>
            </Form.Item>

            <Form.Item name="comments" label="Accounts Comments">
              <TextArea rows={3} placeholder="Add any accounting comments or notes" />
            </Form.Item>
          </>
        );

      case 'production':
        return (
          <>
            <Form.Item
              name="outputQuantity"
              label="Output Quantity"
              rules={[{ required: true, message: 'Please enter output quantity' }]}
            >
              <InputNumber min={0} className="w-full" />
            </Form.Item>
            
            <Form.Item name="damageQuantity" label="Damage Quantity">
              <InputNumber min={0} className="w-full" />
            </Form.Item>
            
            <Form.Item name="comments" label="Comments">
              <TextArea rows={3} placeholder="Enter production comments" />
            </Form.Item>
            
            <Form.Item
              name="status"
              label="Production Status"
              rules={[{ required: true, message: 'Please select status' }]}
            >
              <Select>
                <Option value="PENDING">Pending</Option>
                <Option value="IN_PROGRESS">In Progress</Option>
                <Option value="COMPLETED">Completed</Option>
              </Select>
            </Form.Item>

            {/* File Upload Section */}
            <Form.Item label="Upload Production Files">
              <div style={{ border: '2px dashed #d9d9d9', borderRadius: '6px', padding: '20px', textAlign: 'center' }}>
                <input
                  type="file"
                  multiple
                  onChange={handleFileSelection}
                  style={{ display: 'none' }}
                  id="production-file-upload"
                />
                <label htmlFor="production-file-upload" style={{ cursor: 'pointer' }}>
                  <UploadOutlined style={{ fontSize: '24px', color: '#1890ff', marginBottom: '8px' }} />
                  <div>Click to select production files</div>
                  <div style={{ fontSize: '12px', color: '#999' }}>Photos, documents, reports, etc.</div>
                </label>
              </div>
              {selectedFiles.length > 0 && (
                <div style={{ marginTop: '12px' }}>
                  <Text strong>Selected Files:</Text>
                  {selectedFiles.map((file, index) => (
                    <div key={index} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0' }}>
                      <span>{file.name}</span>
                      <Button size="small" danger onClick={() => removeSelectedFile(index)}>Remove</Button>
                    </div>
                  ))}
                </div>
              )}
            </Form.Item>
          </>
        );

      case 'dispatch':
        return (
          <>
            <Form.Item
              name="finalQuantity"
              label="Final Quantity"
              rules={[{ required: true, message: 'Please enter final quantity' }]}
            >
              <InputNumber min={0} className="w-full" />
            </Form.Item>
            
            <Form.Item name="finalDamage" label="Final Damage">
              <InputNumber min={0} className="w-full" />
            </Form.Item>
            
            <Form.Item name="dispatchMode" label="Dispatch Mode">
              <Select>
                <Option value="BUS">Bus</Option>
                <Option value="TRAIN">Train</Option>
                <Option value="COURIER">Courier</Option>
                <Option value="SELF">Self</Option>
                <Option value="FLIGHT">Flight</Option>
              </Select>
            </Form.Item>
            
            <Form.Item name="weight" label="Weight (kg)">
              <InputNumber min={0} step={0.1} className="w-full" />
            </Form.Item>
            
            <Form.Item name="numberOfBoxes" label="Number of Boxes">
              <InputNumber min={0} className="w-full" />
            </Form.Item>

            <Form.Item name="trackingNumber" label="Tracking Number">
              <Input placeholder="Enter tracking number" />
            </Form.Item>
            
            <Form.Item
              name="status"
              label="Dispatch Status"
              rules={[{ required: true, message: 'Please select status' }]}
            >
              <Select>
                <Option value="PENDING">Pending</Option>
                <Option value="READY_TO_DISPATCH">Ready to Dispatch</Option>
                <Option value="DISPATCHED">Dispatched</Option>
                <Option value="DELIVERED">Delivered</Option>
              </Select>
            </Form.Item>

            <Form.Item name="comments" label="Dispatch Comments">
              <TextArea rows={3} placeholder="Add any dispatch comments or notes" />
            </Form.Item>

            {/* File Upload Section */}
            <Form.Item label="Upload Dispatch Files">
              <div style={{ border: '2px dashed #d9d9d9', borderRadius: '6px', padding: '20px', textAlign: 'center' }}>
                <input
                  type="file"
                  multiple
                  onChange={handleFileSelection}
                  style={{ display: 'none' }}
                  id="dispatch-file-upload"
                />
                <label htmlFor="dispatch-file-upload" style={{ cursor: 'pointer' }}>
                  <UploadOutlined style={{ fontSize: '24px', color: '#1890ff', marginBottom: '8px' }} />
                  <div>Click to select dispatch files</div>
                  <div style={{ fontSize: '12px', color: '#999' }}>Receipts, photos, documents, etc.</div>
                </label>
              </div>
              {selectedFiles.length > 0 && (
                <div style={{ marginTop: '12px' }}>
                  <Text strong>Selected Files:</Text>
                  {selectedFiles.map((file, index) => (
                    <div key={index} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0' }}>
                      <span>{file.name}</span>
                      <Button size="small" danger onClick={() => removeSelectedFile(index)}>Remove</Button>
                    </div>
                  ))}
                </div>
              )}
            </Form.Item>
          </>
        );

      default:
        return null;
    }
  };

  const handlePrintSummary = (job) => {
    setPrintSummaryJob(job);
    setPrintSummaryVisible(true);
  };

  const closePrintSummary = () => {
    setPrintSummaryVisible(false);
    setPrintSummaryJob(null);
  };

  return (
    <div className="p-6">
      <Title level={2}>Job Management Dashboard</Title>
      
      {/* Show filters on all tabs except order-jobs */}
      {activeTab !== 'order-jobs' && <FilterComponent />}
      
      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        {/* Only show dashboard for admin and sales */}
        {(isAdmin || isSales) && (
          <TabPane tab={<span><FileTextOutlined />Dashboard</span>} key="dashboard">
            <DashboardStats />
            <Card title="Jobs Overview" className="mt-4">
              <Table
                dataSource={jobs}
                columns={[
                  {
                    title: 'Job ID',
                    dataIndex: '_id',
                    render: (id) => <Text code>{id.slice(-6)}</Text>,
                    width: 80,
                  },
                  {
                    title: 'Product & Order',
                    dataIndex: 'productCode',
                    render: (code, record) => (
                      <div>
                        <div><strong>{code}</strong></div>
                        <div className="text-xs text-blue-600">#{record.orderId?._id?.slice(-6)}</div>
                        <div className="text-xs text-gray-500">{record.productName}</div>
                      </div>
                    ),
                    width: 150,
                  },
                  {
                    title: 'Client Details',
                    key: 'clientDetails',
                    render: (_, record) => (
                      <div>
                        <div className="font-semibold text-green-700">{record.orderId?.leadId?.name || 'N/A'}</div>
                        <div className="text-xs text-purple-600">{record.orderId?.leadId?.company || 'No Company'}</div>
                        <div className="text-xs flex items-center">
                          <PhoneOutlined className="mr-1" />
                          {record.orderId?.primaryPhone || 'No Phone'}
                        </div>
                        <div className="text-xs text-gray-500">{record.orderId?.leadId?.city || record.orderId?.city || 'N/A'}</div>
                      </div>
                    ),
                    width: 180,
                  },
                  {
                    title: 'Status & Priority',
                    key: 'statusPriority',
                    render: (_, record) => (
                      <div>
                        <Tag color={getStatusColor(record.currentStatus)}>{record.currentStatus?.replace('_', ' ')}</Tag>
                        <div className="mt-1">
                          <Tag color={getPriorityColor(record.priority)} size="small">{record.priority}</Tag>
                          {record.isUrgent && <Tag color="red" size="small">URGENT</Tag>}
                        </div>
                      </div>
                    ),
                    width: 120,
                  },
                  {
                    title: 'Quantity',
                    dataIndex: 'originalQuantity',
                    width: 80,
                  },
                  {
                    title: 'Expected Dispatch',
                    dataIndex: 'expectedDispatchDate',
                    render: (date) => date ? moment(date).format('DD/MM/YYYY') : 'N/A',
                    width: 120,
                  },
                  {
                    title: 'Actual Dispatch',
                    key: 'actualDispatch',
                    render: (_, record) => {
                      const dispatchDate = record.dispatch?.dispatchDate;
                      return dispatchDate ? moment(dispatchDate).format('DD/MM/YYYY') : 'Not Dispatched';
                    },
                    width: 120,
                  },
                  {
                    title: 'Created',
                    dataIndex: 'createdAt',
                    render: (date) => moment(date).format('DD/MM/YYYY'),
                    width: 100,
                  },
                  {
                    title: 'Actions',
                    key: 'actions',
                    render: (_, record) => (
                      <Space size="small">
                        <Tooltip title="View Full Job">
                          <Button
                            icon={<EyeOutlined />}
                            size="small"
                            onClick={() => handleViewJob(record)}
                          />
                        </Tooltip>
                        <Tooltip title="Copy Job Details">
                          <Button
                            icon={<CopyOutlined />}
                            size="small"
                            onClick={() => handleCopyJob(record)}
                          />
                        </Tooltip>
                        <Tooltip title="WhatsApp Customer">
                          <Button
                            icon={<WhatsAppOutlined />}
                            size="small"
                            style={{ color: '#25D366' }}
                            onClick={() => handleWhatsApp(record)}
                            disabled={!record.orderId?.primaryPhone}
                          />
                        </Tooltip>
                      </Space>
                    ),
                    width: 120,
                    fixed: 'right',
                  },
                ]}
                loading={loading}
                size="small"
                scroll={{ x: 1200 }}
                pagination={{
                  showSizeChanger: true,
                  showQuickJumper: true,
                  showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} jobs`,
                  pageSizeOptions: ['10', '20', '50', '100'],
                  defaultPageSize: 20,
                }}
              />
            </Card>
          </TabPane>
        )}

        {(isAdmin || isDesign) && (
          <TabPane tab={<span><BgColorsOutlined />Design ({designJobs.length})</span>} key="design">
            <div className="mb-4">
              <Alert
                message="Design Department - Phase 1 of Main Flow"
                description="First stage in the production flow: Design → Production → Dispatch. Jobs automatically move to Production when design is completed. Accounts operates independently."
                type="info"
                showIcon
              />
            </div>
            <Table
              dataSource={designJobs}
              columns={designColumns}
              loading={loading}
              rowKey="_id"
              size="small"
              scroll={{ x: 1000 }}
            />
          </TabPane>
        )}

        {(isAdmin || isAccounts) && (
          <TabPane tab={<span><DollarOutlined />Accounts ({accountsJobs.length})</span>} key="accounts">
            <div className="mb-4">
              <Alert
                message="Accounts Department - Independent Operations"
                description="Accounts works independently of the main production flow (Design → Production → Dispatch). Manage reconciliation, proformas, and payments for all jobs regardless of their production status."
                type="warning"
                showIcon
              />
            </div>
            <Table
              dataSource={accountsJobs}
              columns={accountsColumns}
              loading={loading}
              rowKey="_id"
              size="small"
              scroll={{ x: 1200 }}
            />
          </TabPane>
        )}

        {(isAdmin || isProduction) && (
          <TabPane tab={<span><ToolOutlined />Production ({productionJobs.length})</span>} key="production">
            <div className="mb-4">
              <Alert
                message="Production Department - Phase 2 of Main Flow"
                description="Second stage: Design → Production → Dispatch. Jobs move here automatically after design completion. Track quantities, damages, and update status: Pending → In Progress → Completed."
                type="info"
                showIcon
              />
            </div>
            <Table
              dataSource={productionJobs}
              columns={productionColumns}
              loading={loading}
              rowKey="_id"
              size="small"
              scroll={{ x: 1000 }}
            />
          </TabPane>
        )}

        {(isAdmin || isDispatch) && (
          <TabPane tab={<span><CarOutlined />Dispatch ({dispatchJobs.length})</span>} key="dispatch">
            <div className="mb-4">
              <Alert
                message="Dispatch Department - Phase 3 of Main Flow"
                description="Final stage: Design → Production → Dispatch. Jobs move here after production completion. Manage final quantities, damages, dispatch modes, weights, and tracking."
                type="info"
                showIcon
              />
            </div>
            <Table
              dataSource={dispatchJobs}
              columns={dispatchColumns}
              loading={loading}
              rowKey="_id"
              size="small"
              scroll={{ x: 1000 }}
            />
          </TabPane>
        )}

        {filterByOrderId && (
          <TabPane tab={<span><FileTextOutlined />Order Jobs ({orderJobs.length})</span>} key="order-jobs">
            <div className="mb-4">
              <Alert
                message={`Jobs for Order ID: ${filterByOrderId}`}
                description="All jobs created for this specific order. Each product creates a separate job that flows through: Design → Production → Dispatch. Accounts operates independently of this main flow."
                type="success"
                showIcon
                action={
                  <Button size="small" onClick={() => window.history.back()}>
                    Back to Orders
                  </Button>
                }
              />
            </div>
            <Table
              dataSource={orderJobs}
              columns={[
                {
                  title: 'Job ID',
                  dataIndex: '_id',
                  render: (id) => <Text code>{id.slice(-6)}</Text>,
                },
                {
                  title: 'Product',
                  dataIndex: 'productCode',
                  render: (code, record) => (
                    <div>
                      <div><strong>{code}</strong></div>
                      <div className="text-gray-500">{record.productName}</div>
                    </div>
                  ),
                },
                {
                  title: 'Quantity',
                  dataIndex: 'originalQuantity',
                },
                {
                  title: 'Priority',
                  dataIndex: 'priority',
                  render: (priority) => <Tag color={getPriorityColor(priority)}>{priority}</Tag>,
                },
                {
                  title: 'Current Status',
                  dataIndex: 'currentStatus',
                  render: (status) => <Tag color={getStatusColor(status)}>{status.replace('_', ' ')}</Tag>,
                },
                {
                  title: 'Expected Dispatch',
                  dataIndex: 'expectedDispatchDate',
                  render: (date, record) => (
                    <div>
                      <div>{date ? moment(date).format('DD/MM/YYYY') : 'N/A'}</div>
                      {record.isUrgent && <Tag color="red" size="small">URGENT</Tag>}
                    </div>
                  ),
                },
                {
                  title: 'Progress',
                  key: 'progress',
                  render: (_, record) => {
                    const statusOrder = ['PENDING_DESIGN', 'PENDING_ACCOUNTS', 'PENDING_PRODUCTION', 'PENDING_DISPATCH', 'DISPATCHED', 'DELIVERED', 'COMPLETED'];
                    const currentIndex = statusOrder.indexOf(record.currentStatus);
                    const progress = currentIndex >= 0 ? ((currentIndex + 1) / statusOrder.length) * 100 : 0;
                    
                    return (
                      <div style={{ width: 120 }}>
                        <Progress 
                          percent={Math.round(progress)} 
                          size="small" 
                          status={record.currentStatus === 'COMPLETED' ? 'success' : 'active'}
                        />
                      </div>
                    );
                  },
                },
                {
                  title: 'Actions',
                  key: 'actions',
                  render: (_, record) => {
                    const department = record.currentStatus.replace('PENDING_', '').toLowerCase();
                    return (
                      <Space>
                        <Button
                          icon={<EditOutlined />}
                          size="small"
                          onClick={() => openJobDrawer(record, department)}
                          disabled={!record.currentStatus.startsWith('PENDING_')}
                        >
                          Update
                        </Button>
                                                 <Button
                           icon={<EyeOutlined />}
                           size="small"
                           onClick={() => handleViewJob(record)}
                         >
                           View
                         </Button>
                      </Space>
                    );
                  },
                },
              ]}
              loading={loading}
              rowKey="_id"
              size="small"
              pagination={false}
            />
          </TabPane>
        )}
      </Tabs>

      <Drawer
        title={`Update ${selectedJob?.department?.toUpperCase()} - Job ${selectedJob?._id?.slice(-6)}`}
        open={drawerVisible}
        onClose={() => {
          setDrawerVisible(false);
          form.resetFields();
        }}
        width={600}
        footer={
          <div className="flex justify-end gap-2">
            <Button onClick={() => setDrawerVisible(false)}>Cancel</Button>
            <Button 
              type="primary" 
              onClick={() => {
                form.validateFields().then(values => {
                  handleJobUpdate(selectedJob._id, values, selectedJob.department);
                });
              }}
            >
              Update Job
            </Button>
          </div>
        }
      >
        <Form form={form} layout="vertical">
          <div className="mb-4 p-4 bg-gray-50 rounded">
            <Row gutter={16}>
              <Col span={12}>
                <Text strong>Product:</Text> {selectedJob?.productCode}
              </Col>
              <Col span={12}>
                <Text strong>Original Qty:</Text> {selectedJob?.originalQuantity}
              </Col>
            </Row>
            <Row gutter={16} className="mt-2">
              <Col span={12}>
                <Text strong>Priority:</Text> <Tag color={getPriorityColor(selectedJob?.priority)}>{selectedJob?.priority}</Tag>
              </Col>
              <Col span={12}>
                <Text strong>Status:</Text> <Tag color={getStatusColor(selectedJob?.currentStatus)}>{selectedJob?.currentStatus?.replace('_', ' ')}</Tag>
              </Col>
            </Row>
          </div>

          {renderUpdateForm()}
        </Form>
      </Drawer>

      <JobDetailView 
        job={viewingJob} 
        visible={viewDrawerOpen} 
        onClose={setViewDrawerOpen} 
      />

      <DesignJobSummary 
        job={printSummaryJob} 
        visible={printSummaryVisible} 
        onClose={closePrintSummary} 
      />
    </div>
  );
};

export default JobDashboard; 